// Shared schema for every client finance application Elin's arranges as an
// introducer. The historical name of this file is kept for backwards
// compatibility — anything referencing "mortgage schema" still works
// because `MORTGAGE_FORM_SECTIONS` and `sectionsForType()` are preserved.
//
// Field types: text | textarea | email | phone | number | money | date |
//              select | yesno | postcode | address | signature | commitments
// Flags:
//   applicants: true  -> field is captured for 1st AND 2nd applicant
//   onlyFor: "mortgage" | "remortgage"  -> hidden for the other type (legacy)
//   showIf: { field, equals } -> conditional sub-field
//   options: ["..."] for select
//   full: true -> field spans the full width
//   help: "..." -> short helper text under the input
//   required: true -> validation flag
//   hideForFamilies: ["personal", "business"] -> omit for those product families

import { findProduct, isPropertyProduct, productFamily } from "./productCatalog";
import { getProductBlock } from "./productBlocks";

// Shared sections every product collects. The product-specific block is
// inserted between `commitments` and `contacts` by `sectionsForType()`.
const SHARED_HEAD = [
  {
    id: "advice",
    title: "Case & Advice Details",
    fields: [
      { id: "customer_names", label: "Customer Name(s)", type: "text", full: true, required: true },
      { id: "advisor_name", label: "Advisor Name", type: "text" },
      { id: "firm_name", label: "Firm Name", type: "text" },
      { id: "case_manager", label: "Case Manager", type: "text" },
      { id: "fact_find_started", label: "Date Fact Find Started", type: "date" },
      { id: "fact_find_completed", label: "Date Fact Find Completed", type: "date" },
      { id: "disclosure_issued", label: "Date Disclosure Document Issued", type: "date" },
      { id: "head_of_compliance", label: "Head of Compliance / Case In Charge", type: "text" },
      { id: "mortgage_lender", label: "Lender / Funder", type: "text" },
      { id: "reference_number", label: "Reference Number", type: "text" },
      { id: "lender_reference_number", label: "Lender Reference Number", type: "text" },
      { id: "file_completed", label: "File Completed By / Date", type: "text" },
    ],
  },
  {
    id: "scope",
    title: "Scope of Advice",
    fields: [
      { id: "objectives", label: "Objectives", type: "textarea", full: true, help: "A short paragraph summarising what you'd like help with." },
      { id: "mortgage_planning", label: "Finance Planning Discussed", type: "yesno" },
      {
        id: "insurance_review",
        label: "Full Review of Insurance Needs",
        type: "select",
        options: ["Yes", "No", "Not required"],
      },
      { id: "customer_limitations", label: "Customer-imposed limitations / notes", type: "textarea", full: true, help: "Any constraints we should know about — e.g. a maximum monthly budget, preferred lenders to avoid, timing restrictions." },
      { id: "enhanced_support", label: "Personal circumstances requiring enhanced support", type: "textarea", full: true, help: "Health, vulnerability or life events we should take into account when advising you." },
    ],
  },
  {
    id: "applicant",
    title: "Applicant Details",
    applicants: true,
    allowCopy: true,
    fields: [
      { id: "title", label: "Title", type: "text" },
      { id: "first_name", label: "First Name", type: "text", required: true },
      { id: "middle_name", label: "Middle Name", type: "text" },
      { id: "surname", label: "Surname", type: "text", required: true },
      { id: "previous_surname", label: "Previous Surname", type: "text" },
      { id: "mothers_maiden", label: "Mother's Maiden Name", type: "text" },
      { id: "present_address", label: "Present Address", type: "textarea", full: true },
      { id: "postcode", label: "Postcode", type: "postcode" },
      { id: "gender", label: "Gender", type: "text" },
      { id: "date_of_birth", label: "Date of Birth", type: "date" },
      { id: "place_of_birth", label: "Age / Place of Birth", type: "text" },
      { id: "home_phone", label: "Home Telephone", type: "phone" },
      { id: "mobile", label: "Mobile", type: "phone" },
      { id: "work_phone", label: "Work Telephone", type: "phone" },
      { id: "email", label: "Email Address", type: "email" },
      { id: "preferred_contact_method", label: "Preferred Contact Method", type: "text", help: "e.g. phone, email, WhatsApp." },
      { id: "preferred_contact_time", label: "Preferred Contact Time", type: "text", help: "e.g. weekday evenings." },
      { id: "relationship", label: "Relationship to Other Applicant", type: "text" },
      { id: "marital_status", label: "Marital Status", type: "text" },
      { id: "nationality", label: "Nationality", type: "text" },
      { id: "country_of_residence", label: "Country of Residence", type: "text" },
      { id: "dependants", label: "Dependants?", type: "yesno" },
      {
        id: "dependants_detail",
        label: "Dependants — Names & DOBs",
        type: "textarea",
        full: true,
        showIf: { field: "dependants", equals: "Yes" },
      },
      { id: "date_moved_in", label: "Date Moved to Current Address", type: "date" },
      { id: "previous_addresses_list", label: "Previous Address(es)", type: "address_history", full: true, help: "Add every address lived at over the last 3 years, with the dates you moved in and out." },
      { id: "residential_status", label: "Current Residential Status", type: "text", help: "e.g. owner-occupier, tenant, living with family." },
    ],
  },
  {
    id: "employment",
    title: "Employment Details",
    applicants: true,
    allowCopy: true,
    fields: [
      { id: "employment_status", label: "Employment Status", type: "select", options: ["Employed", "Self-Employed", "Retired", "Not Employed"] },
      { id: "employer", label: "Employer / Business Name", type: "text" },
      { id: "business_address", label: "Full Business Address", type: "textarea", full: true },
      { id: "business_phone", label: "Business Telephone", type: "phone" },
      { id: "occupation", label: "Occupation / Job Title", type: "text" },
      { id: "employment_basis", label: "Employment Basis", type: "select", options: ["Permanent", "Temporary", "Contract"] },
      { id: "employment_start", label: "Employment Start Date", type: "date" },
      { id: "probation", label: "Probationary Period Details", type: "text" },
      { id: "retirement_age", label: "Expected Retirement Age", type: "number" },
      { id: "basic_salary", label: "Basic Salary p.a.", type: "money" },
      { id: "national_insurance", label: "National Insurance Number", type: "text", help: "Format: QQ 12 34 56 C" },
      { id: "prev_occupation", label: "Previous Occupation (if current employment < 1 year)", type: "text" },
      { id: "prev_employer", label: "Previous Employer / Business", type: "text" },
      { id: "prev_employment_start", label: "Previous Employment Start Date", type: "date" },
      { id: "prev_employment_leave", label: "Previous Employment Leave Date", type: "date" },
      { id: "further_employment_notes", label: "Further Previous Employment / Notes", type: "textarea", full: true },
    ],
  },
  {
    id: "income_employed",
    title: "Income — Employed",
    applicants: true,
    fields: [
      { id: "basic_salary", label: "Basic Salary p.a.", type: "money" },
      { id: "guaranteed_additional", label: "Guaranteed Additional Income p.a.", type: "money" },
      { id: "bonus_year_1", label: "Annual Bonus — Year 1", type: "money" },
      { id: "bonus_year_2", label: "Annual Bonus — Year 2", type: "money" },
      { id: "comm_month_1", label: "Commission / Overtime — Month 1", type: "money" },
      { id: "comm_month_2", label: "Commission / Overtime — Month 2", type: "money" },
      { id: "comm_month_3", label: "Commission / Overtime — Month 3", type: "money" },
      { id: "other_income", label: "Other Earned Income — specify", type: "text" },
    ],
  },
  {
    id: "income_self_employed",
    title: "Income — Self-Employed",
    applicants: true,
    fields: [
      { id: "status", label: "Status", type: "select", options: ["Sole Trader", "Partnership", "LLP", "Ltd Co."] },
      { id: "percent_owned", label: "% Owned", type: "number" },
      { id: "accounting_year", label: "Accounting Year (DD/MM – DD/MM)", type: "text" },
      { id: "years_accounts", label: "Years of Accounts Available", type: "number" },
      { id: "net_profit_last", label: "Net Profit — Last Year", type: "money" },
      { id: "net_profit_prev", label: "Net Profit — Previous Year", type: "money" },
      { id: "net_profit_before", label: "Net Profit — Year Before", type: "money" },
      { id: "other_income_pa", label: "Other Income p.a.", type: "money" },
      { id: "other_income_source", label: "Source / Details of Other Income", type: "textarea", full: true },
      { id: "total_annual_income", label: "Total Annual Income", type: "money" },
      { id: "monthly_net_income", label: "Monthly Net Income", type: "money" },
      { id: "foreign_currency", label: "Foreign Currency Income?", type: "yesno" },
      { id: "foreign_currency_detail", label: "If yes, specify", type: "text", full: true, showIf: { field: "foreign_currency", equals: "Yes" } },
    ],
  },
  {
    id: "commitments",
    title: "Financial Commitments",
    description: "Exclude current mortgage payments. Include loans, hire purchase, student loans and credit cards.",
    fields: [
      { id: "commitments_table", label: "Commitments", type: "commitments" },
      { id: "credit_card_full", label: "Do you repay any credit card balance in full every month?", type: "yesno", full: true },
      { id: "total_monthly_liabilities", label: "Total Committed Monthly Liabilities", type: "money" },
      { id: "notes", label: "Notes / Additional Commitments", type: "textarea", full: true },
    ],
  },
];

// Legacy mortgage-specific property section (keep for `mortgage` key only).
const LEGACY_MORTGAGE_PROPERTY = {
  id: "property",
  title: "Property to be Purchased / Mortgaged",
  onlyFor: "mortgage",
  fields: [
    { id: "address", label: "Property Address", type: "textarea", full: true },
    { id: "postcode", label: "Postcode", type: "postcode" },
    { id: "purchase_price", label: "Purchase Price", type: "money" },
    { id: "loan_amount", label: "Loan Amount", type: "money" },
    { id: "deposit", label: "Deposit", type: "money" },
    { id: "description", label: "Full Property Description", type: "textarea", full: true },
    { id: "bedrooms", label: "Number of Bedrooms", type: "number" },
    { id: "kitchen", label: "Kitchen", type: "text" },
    { id: "bathroom", label: "Bathroom", type: "text" },
    { id: "garden", label: "Garden", type: "text" },
    { id: "parking", label: "Parking", type: "text" },
    { id: "property_type", label: "Property Type", type: "text", help: "e.g. detached house, flat, bungalow." },
    { id: "year_built", label: "Year Built", type: "number" },
    { id: "lease_years", label: "Lease Years Remaining", type: "number" },
    { id: "service_charge", label: "Service Charge", type: "money" },
    { id: "ground_rent", label: "Ground Rent", type: "money" },
    { id: "epc_rating", label: "EPC Rating", type: "text" },
  ],
};

// Legacy remortgage block (preserved so existing records print exactly as before).
const LEGACY_REMORTGAGE_CURRENT = {
  id: "current_mortgage",
  title: "Current Mortgage Details",
  description: "Complete this section only where applicable.",
  onlyFor: "remortgage",
  fields: [
    { id: "lender", label: "Lender", type: "text" },
    { id: "owners", label: "Owner(s)", type: "text" },
    { id: "property_address", label: "Property Address", type: "textarea", full: true },
    { id: "loan_outstanding", label: "Loan Outstanding", type: "money" },
    { id: "term_remaining", label: "Term Remaining", type: "text", help: "e.g. 18 years 4 months." },
    { id: "buy_to_let", label: "Buy to Let", type: "yesno" },
    { id: "current_rate", label: "Current Interest Rate (%)", type: "number" },
    { id: "monthly_payment", label: "Monthly Mortgage Payment", type: "money" },
    { id: "account_number", label: "Account Number", type: "text" },
    { id: "to_be_redeemed", label: "To be Redeemed?", type: "yesno" },
    { id: "rate_type", label: "Interest Rate Type", type: "text", help: "e.g. 5-year fixed, tracker, SVR." },
    { id: "rate_end_date", label: "Current Rate End Date", type: "date" },
    { id: "second_charge", label: "Second Charge?", type: "yesno" },
    { id: "early_repayment_penalties", label: "Early Repayment / Transfer Penalties", type: "textarea", full: true },
    { id: "erc_end_date", label: "ERC End Date", type: "date" },
    { id: "property_value", label: "Property Value / Sale Price", type: "money" },
    { id: "portable", label: "Portable to New Property?", type: "select", options: ["Yes", "No", "Unknown"] },
    { id: "repayment_method", label: "Repayment Method", type: "text", help: "e.g. capital & interest, interest-only." },
    { id: "notes", label: "Notes", type: "textarea", full: true },
  ],
};

const CONTACTS_SECTION = {
  id: "contacts",
  title: "Estate Agent, Solicitor & Bank Details",
  fields: [
    { id: "estate_agent_name", label: "Estate Agent — Name", type: "text" },
    { id: "estate_agent_address", label: "Estate Agent — Address", type: "textarea", full: true },
    { id: "estate_agent_phone", label: "Estate Agent — Telephone", type: "phone" },
    { id: "estate_agent_email", label: "Estate Agent — Email", type: "email" },
    { id: "solicitor_name", label: "Solicitor — Name", type: "text" },
    { id: "solicitor_address", label: "Solicitor — Address", type: "textarea", full: true },
    { id: "solicitor_phone", label: "Solicitor — Telephone", type: "phone" },
    { id: "solicitor_email", label: "Solicitor — Email", type: "email" },
    { id: "bank_name", label: "Bank", type: "text" },
    { id: "bank_account_number", label: "Account Number", type: "text" },
    { id: "bank_sort_code", label: "Sort Code", type: "text", help: "Format: 12-34-56." },
  ],
};

const BANK_SECTION = {
  id: "contacts",
  title: "Bank Details",
  description: "Only your main bank — used for setting up payments if the application proceeds.",
  fields: [
    { id: "bank_name", label: "Bank", type: "text" },
    { id: "bank_account_number", label: "Account Number", type: "text" },
    { id: "bank_sort_code", label: "Sort Code", type: "text", help: "Format: 12-34-56." },
  ],
};

const REVIEW_SECTION = { id: "review", title: "Review Your Answers", isReview: true, fields: [] };

const DECLARATION_SECTION = {
  id: "declaration",
  title: "Client Declaration & Signature",
  isDeclaration: true,
  declaration: "I/We confirm that the information provided in this form is, to the best of my/our knowledge, complete and accurate, and I/we authorise Elin's Consultancy to act on my/our behalf.",
  fields: [
    { id: "signature_applicant_1", label: "Applicant 1 Signature", type: "signature" },
    { id: "signature_applicant_2", label: "Applicant 2 Signature", type: "signature" },
  ],
};

// Legacy export — the original flat list used by older code (customer forms
// list, etc.). Only mortgage + remortgage sections are included here to
// preserve byte-for-byte behaviour for existing records.
export const MORTGAGE_FORM_SECTIONS = [
  ...SHARED_HEAD.slice(0, 6), // advice, scope, applicant, employment, current_mortgage comes next
  LEGACY_REMORTGAGE_CURRENT,
  SHARED_HEAD[4], // income_employed
  SHARED_HEAD[5], // income_self_employed — note: duplicates kept intentionally for legacy snapshot
  SHARED_HEAD[6], // commitments
  LEGACY_MORTGAGE_PROPERTY,
  CONTACTS_SECTION,
  REVIEW_SECTION,
  DECLARATION_SECTION,
];

/**
 * Build the ordered list of sections for a given product key.
 * - Keeps mortgage / remortgage visually identical to the legacy form.
 * - For all other products inserts the product-specific block from
 *   productBlocks.js between the shared Commitments and Contacts sections.
 * - Hides the full estate-agent block for Business / Personal families and
 *   shows a slim Bank-details-only block instead.
 */
export const sectionsForType = (type) => {
  const key = type || "mortgage";

  // Legacy path for mortgage / remortgage — do not touch the structure.
  if (key === "mortgage" || key === "remortgage") {
    const legacy = [
      SHARED_HEAD[0], // advice
      SHARED_HEAD[1], // scope
      SHARED_HEAD[2], // applicant
      SHARED_HEAD[3], // employment
      ...(key === "remortgage" ? [LEGACY_REMORTGAGE_CURRENT] : []),
      SHARED_HEAD[4], // income_employed
      SHARED_HEAD[5], // income_self_employed
      SHARED_HEAD[6], // commitments
      ...(key === "mortgage" ? [LEGACY_MORTGAGE_PROPERTY] : []),
      CONTACTS_SECTION,
      REVIEW_SECTION,
      DECLARATION_SECTION,
    ];
    return legacy;
  }

  // Dynamic composition for every other product.
  const product = findProduct(key);
  const block = getProductBlock(product.block);
  const family = productFamily(key);
  const bankBlock = isPropertyProduct(key) ? CONTACTS_SECTION : BANK_SECTION;

  const sections = [
    SHARED_HEAD[0], // advice
    SHARED_HEAD[1], // scope
    SHARED_HEAD[2], // applicant
    SHARED_HEAD[3], // employment
    SHARED_HEAD[4], // income_employed
    SHARED_HEAD[5], // income_self_employed
    SHARED_HEAD[6], // commitments
    ...(block ? [block] : []),
    bankBlock,
    REVIEW_SECTION,
    DECLARATION_SECTION,
  ];

  // Business products: hide the "Income — Self-Employed" when it duplicates
  // what the product-specific block already asks for the company.
  if (family === "business") {
    return sections.filter((s) => s.id !== "income_self_employed");
  }

  return sections;
};

export const flattenApplicantFields = (section) => {
  if (!section.applicants) return section.fields;
  return section.fields;
};
